import { createRoot } from "react-dom/client";
import { initPostHog, captureException } from "./lib/posthog";
import { SW_CONFIG } from "./constants/app";
import { assertRuntimeEnv, renderConfigUnavailable } from "./lib/envGuard";

const KIT_LANDING_PATH = "/kit-crianca-interior";
const HOME_PATH = "/";
const normalizedPathname = window.location.pathname.replace(/\/+$/, "") || "/";
const hasMetaTestActive =
  new URLSearchParams(window.location.search).has("meta_test") ||
  (() => {
    try { return !!sessionStorage.getItem("meta_test_code"); } catch { return false; }
  })();

// FASE 1: Analytics não essencial (PostHog) — adiado para depois do load/idle.
// Nas landings públicas, o SDK só entra após interação ou timeout. Os eventos
// continuam enfileirados até lá; a renderização crítica não paga por replay,
// surveys, autocapture ou Web Vitals do PostHog.
if (import.meta.env.PROD) {
  const startPostHog = (lightweight = false) => {
    const idle = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (idle) idle(() => initPostHog({ lightweight }), { timeout: 4000 });
    else setTimeout(() => initPostHog({ lightweight }), 2000);
  };

  const isPublicLanding =
    normalizedPathname === "/" || normalizedPathname === KIT_LANDING_PATH;

  if (isPublicLanding) {
    const interactionEvents = ["pointerdown", "keydown", "touchstart"] as const;
    let landingAnalyticsStarted = false;

    const startLandingPostHog = () => {
      if (landingAnalyticsStarted) return;
      landingAnalyticsStarted = true;
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, startLandingPostHog);
      });
      startPostHog(true);
    };

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, startLandingPostHog, {
        once: true,
        passive: true,
      });
    });

    window.setTimeout(
      startLandingPostHog,
      normalizedPathname === KIT_LANDING_PATH ? 30000 : 15000
    );
  } else if (document.readyState === "complete") {
    startPostHog();
  } else {
    window.addEventListener("load", () => startPostHog(), { once: true });
  }

  // Capturar erros globais não tratados (enfileirados até o SDK carregar)
  window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
      captureException(error, {
        source,
        lineno,
        colno,
      });
    }
    return false;
  };

  window.onunhandledrejection = (event) => {
    if (event.reason instanceof Error) {
      captureException(event.reason, {
        type: 'unhandled_promise_rejection',
      });
    }
  };
}


// FASE 2: as duas landings são públicas e não exigem Supabase no bootstrap.
// As demais árvores continuam protegidas pela mesma guarda de ambiente.
const missingEnv =
  normalizedPathname === KIT_LANDING_PATH || normalizedPathname === HOME_PATH
    ? null
    : assertRuntimeEnv();
if (missingEnv) {
  console.error("[Boot] Missing runtime env:", missingEnv);
  renderConfigUnavailable(missingEnv);
} else {
  const loadAppModule = async () => {
    // Esta landing é autossuficiente: não precisa de auth, Supabase, React Query,
    // tema, tooltips nem dos providers do SaaS. O import direto remove uma etapa
    // serial inteira do caminho crítico do LCP.
    if (normalizedPathname === KIT_LANDING_PATH) {
      // O modo de QA mantém o banner visual sem levar React Router para
      // visitantes normais da landing.
      return hasMetaTestActive
        ? import("./KitCriancaInteriorMetaTestApp.tsx")
        : import("./pages/KitCriancaInteriorLanding.tsx");
    }

    // A home anônima não inicializa Supabase, React Query, AuthProvider,
    // next-themes, toast ou tooltips. Usuários com indício de sessão validam a
    // autenticação por import dinâmico já dentro da árvore pública.
    if (normalizedPathname === HOME_PATH) {
      return import("./PublicHomeApp.tsx");
    }

    // A aplicação protegida mantém o QueryClient global usado pelas integrações.
    const [{ queryClient }, appModule] = await Promise.all([
      import("./lib/queryClient"),
      import("./App.tsx"),
    ]);
    (window as Window & { __reactQueryClient?: unknown }).__reactQueryClient = queryClient;
    return appModule;
  };

  loadAppModule()
    .then(({ default: App }) => {
      createRoot(document.getElementById("root")!).render(<App />);
    })
    .catch((error) => {
      console.error("[Boot] Failed to load application:", error);
      renderConfigUnavailable("application_boot_failed");
    });
}

// FASE 3: Registrar Service Worker para cache agressivo (apenas em produção)
// 🧪 Modo teste Meta: NÃO registra SW (e desregistra se já existe) para garantir
// HTML fresh em cada navegação durante QA do Pixel.
// Fonte da flag `meta_test_code`: bootstrap inline em `index.html` (única fonte de gravação).
// Preview/sandbox do Lovable: o build roda em modo PROD, então o SW ficava ativo
// no preview e provocava reloads automáticos a cada edição (chunk antigo em cache
// + controllerchange). Nesses hosts nunca registramos SW.
const isLovablePreviewHost = (() => {
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".lovableproject.com") ||
    h.endsWith(".sandbox.lovable.dev") ||
    h.endsWith(".lovable.app")
  );
})();

const swDisabled = hasMetaTestActive || isLovablePreviewHost;
const swResetFlagKey = hasMetaTestActive ? "meta_test_sw_reset_done" : "preview_sw_reset_done";

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  if (swDisabled) {
    // 🧪 Teardown completo do SW em modo teste Meta:
    // se houver SW controlando a aba atual, limpa caches + unregister + 1 reload
    // automático controlado (com flag para evitar loop). Isso elimina a necessidade
    // de Cmd+Shift+R manual quando o usuário entra em modo teste em uma aba que
    // já estava sob controle de uma versão anterior do SW.
    const resetDone = (() => {
      try { return sessionStorage.getItem(swResetFlagKey) === "1"; } catch { return false; }
    })();
    const hasController = !!navigator.serviceWorker.controller;

    if (hasController && !resetDone) {

      console.log(
        "%c[SW] 🧪 controller detectado em modo teste — limpando e recarregando uma vez",
        "background:#dc2626;color:#fff;padding:4px 8px;border-radius:3px;font-weight:bold;"
      );
      (async () => {
        try {
          // 1) Limpar todos os caches do browser
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
          // 2) Pedir para o SW limpar internamente também (defensivo)
          try { navigator.serviceWorker.controller?.postMessage("CLEAR_CACHE"); } catch { /* opcional */ }
          // 3) Unregister de todos os SWs
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
          // 4) Marcar flag e recarregar UMA vez (sem entrar em loop)
          try { sessionStorage.setItem(swResetFlagKey, "1"); } catch { /* storage opcional */ }
          window.location.replace(window.location.href);
        } catch (e) {
          console.error("[SW] erro no teardown de modo teste:", e);
        }
      })();
    } else if (resetDone && !hasController) {
      console.log(
        "%c[SW] 🧪 modo teste limpo — seguindo sem service worker",
        "background:#16a34a;color:#fff;padding:4px 8px;border-radius:3px;"
      );
    } else if (!hasController) {
      console.log(
        "%c[SW] 🧪 modo teste ativo, sem SW prévio — bootstrap direto",
        "background:#0ea5e9;color:#fff;padding:4px 8px;border-radius:3px;"
      );
    }
    // Em modo teste NUNCA registramos um SW novo — garante HTML/JS sempre fresh.
  } else {
    // Sem modo teste: limpar flags residuais de teardown e seguir registro normal do SW.
    try { sessionStorage.removeItem("meta_test_sw_reset_done"); } catch { /* storage opcional */ }
    try { sessionStorage.removeItem("preview_sw_reset_done"); } catch { /* storage opcional */ }
    window.addEventListener("load", () => {
      // Na primeira instalação não havia controller antes do registro.
      // O clients.claim() do SW emite controllerchange, mas recarregar aqui
      // repetiria todo o bootstrap da página (incluindo GTM/GA4/Meta).
      const hadControllerBeforeRegistration = !!navigator.serviceWorker.controller;

      navigator.serviceWorker
        .register(`/sw.js?v=${SW_CONFIG.VERSION}`)
        .then((registration) => {
          console.log("[SW] Registered successfully:", registration.scope);

          // Recarregar apenas quando o usuário escolher atualizar (via hook useServiceWorkerUpdate)
          let refreshing = false;
          navigator.serviceWorker.addEventListener("controllerchange", () => {
            // Primeira instalação: o worker assume controle sem recarregar a página.
            // Atualização de um worker já ativo: mantém o reload único existente.
            if (!hadControllerBeforeRegistration || refreshing) return;
            refreshing = true;
            window.location.reload();
          });

          // Se já houver um SW aguardando, apenas notificar o usuário (sem reload automático)
          if (registration.waiting) {
            window.dispatchEvent(new CustomEvent("sw-update-available"));
          }

          // Detectar updates e apenas notificar (ativação só via ação explícita do usuário)
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                window.dispatchEvent(new CustomEvent("sw-update-available"));
              }
            });
          });

          // Verificar atualizações periodicamente
          setInterval(() => {
            registration.update();
          }, SW_CONFIG.UPDATE_CHECK_INTERVAL);
        })
        .catch((error) => {
          console.error("[SW] Registration failed:", error);
        });
    });
  }
}
